import { useEffect, useState } from 'react';

/**
 * Run timing (observation 8): every step of a deployment, setup or
 * rollback shows when it started, when it finished and how long it took,
 * and a running step counts up live. The times are the engine's own
 * started_at/completed_at; nothing here is estimated.
 */

export interface Timed {
  status: string;
  started_at?: string | null;
  completed_at?: string | null;
}

// Execution, step and deployment statuses that mean "still going".
const RUNNING = new Set(['running', 'preflighting', 'building', 'deploying', 'verifying', 'activating', 'post_deploy', 'rolling_back', 'in_progress', 'compensating']);

export function isRunningStatus(status: string): boolean {
  return RUNNING.has(status);
}

function ms(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const value = Date.parse(iso);
  return Number.isFinite(value) ? value : null;
}

/** A duration as people say it: "under a second", "12 s", "3 min 05 s", "1 h 02 min". */
export function formatDuration(milliseconds: number): string {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) return '';
  if (milliseconds < 1000) return 'under a second';
  const seconds = Math.floor(milliseconds / 1000);
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ${String(seconds % 60).padStart(2, '0')} s`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${String(minutes % 60).padStart(2, '0')} min`;
}

/** A clock reading for a live counter: "0:42", "12:05", "1:02:09". */
export function formatClock(milliseconds: number): string {
  const total = Math.max(0, Math.floor(milliseconds / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

export interface Timing {
  started: number | null;
  finished: number | null;
  /** Finished minus started, or now minus started while it runs. */
  elapsed: number | null;
  running: boolean;
}

export function timingOf(item: Timed, now: number): Timing {
  const started = ms(item.started_at);
  const finished = ms(item.completed_at);
  const running = RUNNING.has(item.status) && finished === null;
  let elapsed: number | null = null;
  if (started !== null && finished !== null) elapsed = Math.max(0, finished - started);
  else if (started !== null && running) elapsed = Math.max(0, now - started);
  return { started, finished, elapsed, running };
}

/** Re-renders once a second while `active`, for live counters. */
export function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

const timeOfDay = (value: number) => new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

/** "Started 10:03:12 · finished 10:03:24", for a tooltip and screen readers. */
export function timingSentence(t: Timing): string {
  if (t.started === null) return 'Not started';
  const start = `Started ${timeOfDay(t.started)}`;
  if (t.finished !== null) return `${start}, finished ${timeOfDay(t.finished)}`;
  return t.running ? `${start}, still running` : start;
}

/**
 * StepTime: the duration of one step, right of its title. A running step
 * counts up; a step that never started shows nothing.
 */
export function StepTime({ step, now }: { step: Timed; now: number }) {
  const t = timingOf(step, now);
  if (t.started === null || t.elapsed === null) return null;
  const sentence = timingSentence(t);
  return (
    <time
      className={`run-time tabular-nums${t.running ? ' run-time--live' : ''}`}
      dateTime={`PT${Math.round(t.elapsed / 1000)}S`}
      title={sentence}
      data-testid="step-time"
    >
      {t.running ? formatClock(t.elapsed) : formatDuration(t.elapsed)}
      <span className="visually-hidden">. {sentence}</span>
    </time>
  );
}

/**
 * RunTiming: the run's total, under its title: "Took 4 min 12 s" once it
 * finished, "Running for 1:03" while it runs, with the start and end time.
 */
export function RunTiming({ run, now }: { run: Timed; now: number }) {
  const t = timingOf(run, now);
  if (t.started === null) return <span className="run-timing" data-testid="run-timing">Not started yet</span>;
  return (
    <span className="run-timing" data-testid="run-timing">
      {t.running ? (
        <>
          Running for <strong className="tabular-nums">{formatClock(t.elapsed ?? 0)}</strong>, started {timeOfDay(t.started)}
        </>
      ) : t.finished !== null ? (
        <>
          Took <strong className="tabular-nums">{formatDuration(t.elapsed ?? 0)}</strong>, {timeOfDay(t.started)} to {timeOfDay(t.finished)}
        </>
      ) : (
        <>Started {timeOfDay(t.started)}</>
      )}
    </span>
  );
}
